import { useEffect, useMemo, useState } from "react";
import "./admin.css";
import { getCurrentAdmin, login, logout } from "../api/auth";
import { api } from "../api/axios";
import { clearSession, getAdminToken, readStoredUser, storeSession } from "./session";
import {
  createAdminNavigationItem,
  createAdminPageTableColumn,
  deleteAdminNavigationItem,
  deleteAdminPageTableColumn,
  getAdminNavigation,
  getAdminPageSections,
  getAdminPageTableColumns,
  getAdminSiteSettings,
  reorderAdminNavigationItems,
  reorderAdminPageTableColumns,
  updateAdminNavigationItem,
  updateAdminPageSection,
  updateAdminPageTableColumn,
  updateAdminSiteSettings,
} from "../api/content";
import { createProperty, deleteProperty, getProperties, updateProperty } from "../api/properties";
import { createUser, getUsers, updateUser } from "../api/users";

const adminNavItems = [
  { path: "/admin", label: "Dashboard" },
  { path: "/admin/general", label: "General" },
  { path: "/admin/navigation", label: "Navigation" },
  { path: "/admin/homepage-hero", label: "Homepage Hero" },
  { path: "/admin/homepage-property-intro", label: "Property Intro" },
  { path: "/admin/homepage-investor-focus", label: "Investor Focus" },
  { path: "/admin/homepage-table-columns", label: "Table Columns" },
  { path: "/admin/articles", label: "Articles" },
  { path: "/admin/news", label: "News" },
  { path: "/admin/properties", label: "Properties" },
  { path: "/admin/admins", label: "Admins" },
];

const generalDefaults = {
  site_name: "Trophy NNN Assets",
  logo_image_url: "",
  logo_alt_text: "Trophy NNN Assets logo",
  logo_title: "Trophy NNN Assets",
  logo_subtitle: "American Realty Network LLC",
  primary_phone: "561-702-3795",
  primary_cta_label: "Call or Text",
  primary_cta_link: "/contact",
  footer_brand: "Cap Rate Experts",
  footer_description:
    "Investor-focused advisory and brokerage support for NNN and Absolute NNN acquisitions across the United States, including high CAP investment opportunities and 1031 exchange solutions.",
  footer_copyright: "© 2026 American Realty Network LLC. All rights reserved.",
};

const emptyNavigationForm = {
  label: "",
  path: "",
  page_key: "",
  is_visible: true,
  open_in_new_tab: false,
};

const emptyColumnForm = {
  column_key: "",
  label: "",
  source_field: "",
  is_visible: true,
  is_sortable: false,
};

const articleDefaults = {
  category: "",
  title: "",
  excerpt: "",
  content: "",
  external_url: "",
  featured_image_url: "",
  slug: "",
  author_name: "",
  status: "published",
  is_featured: true,
  published_at: "",
};

const newsDefaults = {
  title: "",
  body: "",
  link_url: "",
  is_visible: true,
  published_at: "",
};

const emptyPropertyForm = {
  property_code: "",
  title: "",
  asking_price: "",
  cap_rate: "",
  net_rent_monthly: "",
  tenant_name: "",
  lease_expires_at: "",
  rent_type: "",
  landlord_responsibility: "",
  year_built: "",
  city: "",
  state: "",
  status: "draft",
  description: "",
};

const emptyAdminForm = {
  full_name: "",
  email: "",
  password: "",
  role: "admin",
  is_active: true,
};

const sourceFieldOptions = [
  "askingPrice",
  "capRate",
  "tenant",
  "netRent",
  "expires",
  "rentType",
  "landlord",
  "built",
];

const isAdminRoute = (path) => path === "/login" || path.startsWith("/admin");

const moveItem = (items, index, direction) => {
  const next = [...items];
  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= next.length) return next;
  [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
  return next;
};



function AdminLoginPage({ onNavigate, onSessionChange }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (getAdminToken()) onNavigate("/admin");
  }, [onNavigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const payload = await login(email, password);
      storeSession(payload);
      onSessionChange(payload.user);
      onNavigate("/admin");
    } catch (requestError) {
      setError(requestError.message || "Unable to sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        

        <div className="login-card-panel login-card-panel-form">
          <p className="login-eyebrow">Sign in</p>
          
          <p className="login-form-copy">Use your admin email and password to open the site dashboard.</p>
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            {error ? <div className="login-error">{error}</div> : null}
            <button type="submit" disabled={loading}>
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function AdminLayout({ currentPath, onNavigate, user, onLogout, children }) {
  return (
    <div className="super-admin-layout admin-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="sidebar-logo">TNA</span>
          <span className="sidebar-title">Site Admin Panel</span>
        </div>
        <hr className="sidebar-divider" />
        <nav className="sidebar-nav">
          {adminNavItems.map((item) => (
            <button
              key={item.path}
              type="button"
              className={currentPath === item.path ? "admin-nav-link active" : "admin-nav-link"}
              onClick={() => onNavigate(item.path)}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </aside>

      <div className="main-wrap">
        <header className="main-header">
          <div className="header-user">
            <div className="header-name">{user?.fullName || "Admin user"}</div>
            <div className="header-community">TrophyNNNAssets.com</div>
          </div>
          <button type="button" className="logout-btn" onClick={onLogout}>
            Log out
          </button>
        </header>

        <main className="main-content">
          <div className="main-content-inner">{children}</div>
        </main>
      </div>
    </div>
  );
}

function DashboardPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let ignore = false;

    api.get("/api/admin/dashboard")
      .then((response) => response.data)
      .then((payload) => {
        if (!ignore) setSummary(payload);
      })
      .catch((requestError) => {
        if (!ignore) setError(requestError.message || "Failed to load dashboard");
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, []);

  if (loading) return <div className="page-loading">Loading dashboard...</div>;
  if (error) return <div className="page-error">{error}</div>;

  const metrics = summary?.metrics || {};

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-hint">
            The site now has dedicated backend structures for global settings, navigation, page sections,
            table columns, articles, and news.
          </p>
        </div>
      </div>

      <section className="metrics-grid">
        <article className="metric-card">
          <span className="metric-label">Total properties</span>
          <strong>{metrics.propertyCount || 0}</strong>
        </article>
        <article className="metric-card">
          <span className="metric-label">Active listings</span>
          <strong>{metrics.activePropertyCount || 0}</strong>
        </article>
        <article className="metric-card">
          <span className="metric-label">Admins</span>
          <strong>{metrics.adminCount || 0}</strong>
        </article>
        <article className="metric-card">
          <span className="metric-label">Questionnaire leads</span>
          <strong>{metrics.leadCount || 0}</strong>
        </article>
      </section>

      <section className="admin-card">
        <div className="admin-card-header">
          <h2>Recent questionnaire activity</h2>
        </div>
        <div className="table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Email</th>
                <th>Status</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.recentLeads || []).map((lead) => (
                <tr key={lead.leadId}>
                  <td>{lead.name}</td>
                  <td>{lead.email || "No email yet"}</td>
                  <td>
                    <span className={`status-pill ${lead.status}`}>{lead.status}</span>
                  </td>
                  <td>{lead.updatedAt ? new Date(lead.updatedAt).toLocaleString() : "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function GeneralSettingsPage() {
  const [form, setForm] = useState(generalDefaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    let ignore = false;

    getAdminSiteSettings()
      .then((payload) => {
        if (!ignore) setForm({ ...generalDefaults, ...(payload || {}) });
      })
      .catch((requestError) => {
        if (!ignore) setError(requestError.message || "Failed to load site settings");
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await updateAdminSiteSettings(form);
      setSuccess("General site settings saved.");
    } catch (requestError) {
      setError(requestError.message || "Failed to save site settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-loading">Loading general settings...</div>;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">General</h1>
          <p className="page-hint">Edit the logo block, call button, phone number, and footer content.</p>
        </div>
      </div>

      <form className="admin-form-grid" onSubmit={handleSubmit}>
        <section className="admin-card">
          <div className="admin-card-header">
            <h2>Branding</h2>
          </div>
          <div className="form-row">
            <label>Site name</label>
            <input value={form.site_name} onChange={(event) => setForm((current) => ({ ...current, site_name: event.target.value }))} />
          </div>
          <div className="form-row">
            <label>Logo image URL</label>
            <input value={form.logo_image_url} onChange={(event) => setForm((current) => ({ ...current, logo_image_url: event.target.value }))} />
          </div>
          <div className="form-row">
            <label>Logo alt text</label>
            <input value={form.logo_alt_text} onChange={(event) => setForm((current) => ({ ...current, logo_alt_text: event.target.value }))} />
          </div>
          <div className="form-row">
            <label>Logo title</label>
            <input value={form.logo_title} onChange={(event) => setForm((current) => ({ ...current, logo_title: event.target.value }))} />
          </div>
          <div className="form-row">
            <label>Logo subtitle</label>
            <input value={form.logo_subtitle} onChange={(event) => setForm((current) => ({ ...current, logo_subtitle: event.target.value }))} />
          </div>
        </section>

        <section className="admin-card">
          <div className="admin-card-header">
            <h2>Header and footer</h2>
          </div>
          <div className="form-row">
            <label>Primary phone</label>
            <input value={form.primary_phone} onChange={(event) => setForm((current) => ({ ...current, primary_phone: event.target.value }))} />
          </div>
          <div className="form-row">
            <label>Header CTA label</label>
            <input value={form.primary_cta_label} onChange={(event) => setForm((current) => ({ ...current, primary_cta_label: event.target.value }))} />
          </div>
          <div className="form-row">
            <label>Header CTA link</label>
            <input value={form.primary_cta_link} onChange={(event) => setForm((current) => ({ ...current, primary_cta_link: event.target.value }))} />
          </div>
          <div className="form-row">
            <label>Footer brand</label>
            <input value={form.footer_brand} onChange={(event) => setForm((current) => ({ ...current, footer_brand: event.target.value }))} />
          </div>
          <div className="form-row">
            <label>Footer description</label>
            <textarea rows={5} value={form.footer_description} onChange={(event) => setForm((current) => ({ ...current, footer_description: event.target.value }))} />
          </div>
          <div className="form-row">
            <label>Footer copyright</label>
            <input value={form.footer_copyright} onChange={(event) => setForm((current) => ({ ...current, footer_copyright: event.target.value }))} />
          </div>
        </section>

        {error ? <div className="page-error admin-card-full">{error}</div> : null}
        {success ? <div className="page-success admin-card-full">{success}</div> : null}

        <div className="form-actions admin-card-full">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Save general settings"}
          </button>
        </div>
      </form>
    </div>
  );
}

function NavigationPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(emptyNavigationForm);

  const loadItems = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await getAdminNavigation();
      setItems(Array.isArray(payload) ? payload : []);
    } catch (requestError) {
      setError(requestError.message || "Failed to load navigation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const openCreate = () => {
    setEditingId("");
    setForm(emptyNavigationForm);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      label: item.label || "",
      path: item.path || "",
      page_key: item.page_key || "",
      is_visible: item.is_visible !== false,
      open_in_new_tab: Boolean(item.open_in_new_tab),
    });
    setModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editingId) await updateAdminNavigationItem(editingId, form);
      else await createAdminNavigationItem(form);
      setModalOpen(false);
      await loadItems();
    } catch (requestError) {
      setError(requestError.message || "Failed to save navigation item");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this navigation item?")) return;
    try {
      await deleteAdminNavigationItem(id);
      await loadItems();
    } catch (requestError) {
      setError(requestError.message || "Failed to delete navigation item");
    }
  };

  const handleMove = async (index, direction) => {
    const next = moveItem(items, index, direction);
    setItems(next);
    try {
      await reorderAdminNavigationItems(next.map((item) => ({ id: item.id })));
    } catch (requestError) {
      setError(requestError.message || "Failed to reorder navigation");
      await loadItems();
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">Navigation</h1>
          <p className="page-hint">Control menu labels, routes, visibility, and order in the header.</p>
        </div>
        <button type="button" className="btn-primary" onClick={openCreate}>
          Add nav item
        </button>
      </div>

      {loading ? <div className="page-loading">Loading navigation...</div> : null}
      {error ? <div className="page-error">{error}</div> : null}

      {!loading ? (
        <section className="admin-card">
          <div className="table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Path</th>
                  <th>Page key</th>
                  <th>Visible</th>
                  <th>Order</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={item.id}>
                    <td>{item.label}</td>
                    <td>{item.path}</td>
                    <td>{item.page_key || "—"}</td>
                    <td>{item.is_visible !== false ? "Yes" : "No"}</td>
                    <td className="row-actions">
                      <button type="button" onClick={() => handleMove(index, -1)} disabled={index === 0}>
                        Up
                      </button>
                      <button type="button" onClick={() => handleMove(index, 1)} disabled={index === items.length - 1}>
                        Down
                      </button>
                    </td>
                    <td className="row-actions">
                      <button type="button" onClick={() => openEdit(item)}>
                        Edit
                      </button>
                      <button type="button" className="danger-btn" onClick={() => handleDelete(item.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {modalOpen ? (
        <div className="modal-overlay" onClick={() => !saving && setModalOpen(false)}>
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <h3>{editingId ? "Edit navigation item" : "Add navigation item"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <label>Label</label>
                <input required value={form.label} onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))} />
              </div>
              <div className="form-row">
                <label>Path</label>
                <input required value={form.path} onChange={(event) => setForm((current) => ({ ...current, path: event.target.value }))} />
              </div>
              <div className="form-row">
                <label>Page key</label>
                <input value={form.page_key} onChange={(event) => setForm((current) => ({ ...current, page_key: event.target.value }))} />
              </div>
              <label className="checkbox-row">
                <input type="checkbox" checked={form.is_visible} onChange={(event) => setForm((current) => ({ ...current, is_visible: event.target.checked }))} />
                <span>Visible in menu</span>
              </label>
              <label className="checkbox-row">
                <input type="checkbox" checked={form.open_in_new_tab} onChange={(event) => setForm((current) => ({ ...current, open_in_new_tab: event.target.checked }))} />
                <span>Open in new tab</span>
              </label>
              <div className="form-actions">
                <button type="button" onClick={() => setModalOpen(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Save changes" : "Create nav item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SectionEditorPage({ pageKey, sectionKey, title, description, fields, sectionType = "content" }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    let ignore = false;

    getAdminPageSections(pageKey)
      .then((payload) => {
        if (ignore) return;
        const list = Array.isArray(payload) ? payload : [];
        const section = list.find((item) => item.section_key === sectionKey);
        const next = {};

        fields.forEach((field) => {
          if (field.type === "list") {
            const incoming = section?.payload_json?.[field.name];
            const fallback = field.defaultValue || [];
            next[field.name] = Array.isArray(incoming) && incoming.length ? incoming : fallback;
          } else {
            next[field.name] = section?.payload_json?.[field.name] ?? field.defaultValue ?? "";
          }
        });

        setForm(next);
      })
      .catch((requestError) => {
        if (!ignore) setError(requestError.message || "Failed to load section");
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [fields, pageKey, sectionKey]);

  const updateField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const updateListItem = (name, index, value) => {
    setForm((current) => {
      const nextList = [...(current[name] || [])];
      nextList[index] = value;
      return { ...current, [name]: nextList };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await updateAdminPageSection(pageKey, sectionKey, {
        section_type: sectionType,
        title,
        payload_json: form,
        is_enabled: true,
      });
      setSuccess(`${title} saved.`);
    } catch (requestError) {
      setError(requestError.message || "Failed to save section");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-loading">Loading section...</div>;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">{title}</h1>
          <p className="page-hint">{description}</p>
        </div>
      </div>

      <form className="admin-form-grid" onSubmit={handleSubmit}>
        <section className="admin-card admin-card-full">
          {fields.map((field) => {
            if (field.type === "list") {
              const values = form[field.name] || field.defaultValue || [];
              return (
                <div className="form-row" key={field.name}>
                  <label>{field.label}</label>
                  <div className="stack-list">
                    {values.map((value, index) => (
                      <input
                        key={`${field.name}-${index}`}
                        value={value}
                        onChange={(event) => updateListItem(field.name, index, event.target.value)}
                        placeholder={`${field.itemLabel || field.label} ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              );
            }

            if (field.type === "textarea") {
              return (
                <div className="form-row" key={field.name}>
                  <label>{field.label}</label>
                  <textarea
                    rows={field.rows || 4}
                    value={form[field.name] || ""}
                    onChange={(event) => updateField(field.name, event.target.value)}
                  />
                </div>
              );
            }

            return (
              <div className="form-row" key={field.name}>
                <label>{field.label}</label>
                <input
                  type={field.type || "text"}
                  value={form[field.name] || ""}
                  onChange={(event) => updateField(field.name, event.target.value)}
                />
              </div>
            );
          })}
        </section>

        {error ? <div className="page-error admin-card-full">{error}</div> : null}
        {success ? <div className="page-success admin-card-full">{success}</div> : null}

        <div className="form-actions admin-card-full">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : `Save ${title}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function TableColumnsPage() {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(emptyColumnForm);

  const loadColumns = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await getAdminPageTableColumns("home");
      setColumns(Array.isArray(payload) ? payload : []);
    } catch (requestError) {
      setError(requestError.message || "Failed to load table columns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadColumns();
  }, []);

  const openCreate = () => {
    setEditingId("");
    setForm(emptyColumnForm);
    setModalOpen(true);
  };

  const openEdit = (column) => {
    setEditingId(column.id);
    setForm({
      column_key: column.column_key || "",
      label: column.label || "",
      source_field: column.source_field || "",
      is_visible: column.is_visible !== false,
      is_sortable: Boolean(column.is_sortable),
    });
    setModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editingId) await updateAdminPageTableColumn("home", editingId, form);
      else await createAdminPageTableColumn("home", form);
      setModalOpen(false);
      await loadColumns();
    } catch (requestError) {
      setError(requestError.message || "Failed to save table column");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this table column?")) return;
    try {
      await deleteAdminPageTableColumn("home", id);
      await loadColumns();
    } catch (requestError) {
      setError(requestError.message || "Failed to delete table column");
    }
  };

  const handleMove = async (index, direction) => {
    const next = moveItem(columns, index, direction);
    setColumns(next);
    try {
      await reorderAdminPageTableColumns("home", next.map((item) => ({ id: item.id })));
    } catch (requestError) {
      setError(requestError.message || "Failed to reorder columns");
      await loadColumns();
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">Homepage Table Columns</h1>
          <p className="page-hint">
            Pick which listing columns show on the homepage table and control their order and labels.
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={openCreate}>
          Add column
        </button>
      </div>

      {loading ? <div className="page-loading">Loading table columns...</div> : null}
      {error ? <div className="page-error">{error}</div> : null}

      {!loading ? (
        <section className="admin-card">
          <div className="table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Source field</th>
                  <th>Visible</th>
                  <th>Sortable</th>
                  <th>Order</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {columns.map((column, index) => (
                  <tr key={column.id}>
                    <td>{column.label}</td>
                    <td>{column.source_field}</td>
                    <td>{column.is_visible !== false ? "Yes" : "No"}</td>
                    <td>{column.is_sortable ? "Yes" : "No"}</td>
                    <td className="row-actions">
                      <button type="button" onClick={() => handleMove(index, -1)} disabled={index === 0}>
                        Up
                      </button>
                      <button type="button" onClick={() => handleMove(index, 1)} disabled={index === columns.length - 1}>
                        Down
                      </button>
                    </td>
                    <td className="row-actions">
                      <button type="button" onClick={() => openEdit(column)}>
                        Edit
                      </button>
                      <button type="button" className="danger-btn" onClick={() => handleDelete(column.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {modalOpen ? (
        <div className="modal-overlay" onClick={() => !saving && setModalOpen(false)}>
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <h3>{editingId ? "Edit table column" : "Add table column"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <label>Column key</label>
                <input required value={form.column_key} onChange={(event) => setForm((current) => ({ ...current, column_key: event.target.value }))} />
              </div>
              <div className="form-row">
                <label>Label</label>
                <input required value={form.label} onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))} />
              </div>
              <div className="form-row">
                <label>Source field</label>
                <select value={form.source_field} onChange={(event) => setForm((current) => ({ ...current, source_field: event.target.value }))}>
                  <option value="">Select a field</option>
                  {sourceFieldOptions.map((field) => (
                    <option key={field} value={field}>
                      {field}
                    </option>
                  ))}
                </select>
              </div>
              <label className="checkbox-row">
                <input type="checkbox" checked={form.is_visible} onChange={(event) => setForm((current) => ({ ...current, is_visible: event.target.checked }))} />
                <span>Visible on homepage</span>
              </label>
              <label className="checkbox-row">
                <input type="checkbox" checked={form.is_sortable} onChange={(event) => setForm((current) => ({ ...current, is_sortable: event.target.checked }))} />
                <span>Sortable column</span>
              </label>
              <div className="form-actions">
                <button type="button" onClick={() => setModalOpen(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Save changes" : "Create column"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ArticlesPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(articleDefaults);

  const loadArticles = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/api/admin/articles");
      const payload = response.data;
      setArticles(Array.isArray(payload) ? payload : []);
    } catch (requestError) {
      setError(requestError.message || "Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArticles();
  }, []);

  const openCreate = () => {
    setEditingId("");
    setForm(articleDefaults);
    setModalOpen(true);
  };

  const openEdit = (article) => {
    setEditingId(article.id);
    setForm({
      category: article.category || "",
      title: article.title || "",
      excerpt: article.excerpt || "",
      content: article.content || "",
      external_url: article.external_url || "",
      featured_image_url: article.featured_image_url || "",
      slug: article.slug || "",
      author_name: article.author_name || "",
      status: article.status || "published",
      is_featured: Boolean(article.is_featured),
      published_at: article.published_at ? String(article.published_at).slice(0, 10) : "",
    });
    setModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editingId) await api.put(`/api/admin/articles/${editingId}`, form);
      else await api.post("/api/admin/articles", form);
      setModalOpen(false);
      await loadArticles();
    } catch (requestError) {
      setError(requestError.message || "Failed to save article");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await api.delete(`/api/admin/articles/${id}`);
      await loadArticles();
    } catch (requestError) {
      setError(requestError.message || "Failed to delete article");
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">Articles</h1>
          <p className="page-hint">Manage the article cards rendered on the homepage.</p>
        </div>
        <button type="button" className="btn-primary" onClick={openCreate}>
          Add article
        </button>
      </div>

      {loading ? <div className="page-loading">Loading articles...</div> : null}
      {error ? <div className="page-error">{error}</div> : null}

      {!loading ? (
        <section className="admin-card">
          <div className="table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Published</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => (
                  <tr key={article.id}>
                    <td>{article.category}</td>
                    <td>{article.title}</td>
                    <td>{article.status}</td>
                    <td>{article.published_at ? String(article.published_at).slice(0, 10) : "N/A"}</td>
                    <td className="row-actions">
                      <button type="button" onClick={() => openEdit(article)}>
                        Edit
                      </button>
                      <button type="button" className="danger-btn" onClick={() => handleDelete(article.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {modalOpen ? (
        <div className="modal-overlay" onClick={() => !saving && setModalOpen(false)}>
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <h3>{editingId ? "Edit article" : "Add article"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="subcard-grid">
                <div className="form-row">
                  <label>Category</label>
                  <input value={form.category} onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Status</label>
                  <select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
                <div className="form-row">
                  <label>Title</label>
                  <input required value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Slug</label>
                  <input value={form.slug} onChange={(event) => setForm((current) => ({ ...current, slug: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>External URL</label>
                  <input value={form.external_url} onChange={(event) => setForm((current) => ({ ...current, external_url: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Published date</label>
                  <input type="date" value={form.published_at} onChange={(event) => setForm((current) => ({ ...current, published_at: event.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <label>Excerpt</label>
                <textarea rows={4} value={form.excerpt} onChange={(event) => setForm((current) => ({ ...current, excerpt: event.target.value }))} />
              </div>
              <div className="form-row">
                <label>Content</label>
                <textarea rows={6} value={form.content} onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))} />
              </div>
              <div className="form-row">
                <label>Featured image URL</label>
                <input value={form.featured_image_url} onChange={(event) => setForm((current) => ({ ...current, featured_image_url: event.target.value }))} />
              </div>
              <label className="checkbox-row">
                <input type="checkbox" checked={form.is_featured} onChange={(event) => setForm((current) => ({ ...current, is_featured: event.target.checked }))} />
                <span>Show on homepage</span>
              </label>
              <div className="form-actions">
                <button type="button" onClick={() => setModalOpen(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Save changes" : "Create article"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function NewsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(newsDefaults);

  const loadItems = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/api/admin/news");
      const payload = response.data;
      setItems(Array.isArray(payload) ? payload : []);
    } catch (requestError) {
      setError(requestError.message || "Failed to load news");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const openCreate = () => {
    setEditingId("");
    setForm(newsDefaults);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      body: item.body || "",
      link_url: item.link_url || "",
      is_visible: item.is_visible !== false,
      published_at: item.published_at ? String(item.published_at).slice(0, 10) : "",
    });
    setModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editingId) await api.put(`/api/admin/news/${editingId}`, form);
      else await api.post("/api/admin/news", form);
      setModalOpen(false);
      await loadItems();
    } catch (requestError) {
      setError(requestError.message || "Failed to save news item");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this news item?")) return;
    try {
      await api.delete(`/api/admin/news/${id}`);
      await loadItems();
    } catch (requestError) {
      setError(requestError.message || "Failed to delete news item");
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">News</h1>
          <p className="page-hint">Manage the market briefing/news cards rendered on the homepage sidebar.</p>
        </div>
        <button type="button" className="btn-primary" onClick={openCreate}>
          Add news item
        </button>
      </div>

      {loading ? <div className="page-loading">Loading news...</div> : null}
      {error ? <div className="page-error">{error}</div> : null}

      {!loading ? (
        <section className="admin-card">
          <div className="table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Visible</th>
                  <th>Published</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.title}</td>
                    <td>{item.is_visible !== false ? "Yes" : "No"}</td>
                    <td>{item.published_at ? String(item.published_at).slice(0, 10) : "N/A"}</td>
                    <td className="row-actions">
                      <button type="button" onClick={() => openEdit(item)}>
                        Edit
                      </button>
                      <button type="button" className="danger-btn" onClick={() => handleDelete(item.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {modalOpen ? (
        <div className="modal-overlay" onClick={() => !saving && setModalOpen(false)}>
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <h3>{editingId ? "Edit news item" : "Add news item"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <label>Title</label>
                <input required value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} />
              </div>
              <div className="form-row">
                <label>Body</label>
                <textarea rows={5} value={form.body} onChange={(event) => setForm((current) => ({ ...current, body: event.target.value }))} />
              </div>
              <div className="form-row">
                <label>Link URL</label>
                <input value={form.link_url} onChange={(event) => setForm((current) => ({ ...current, link_url: event.target.value }))} />
              </div>
              <div className="form-row">
                <label>Published date</label>
                <input type="date" value={form.published_at} onChange={(event) => setForm((current) => ({ ...current, published_at: event.target.value }))} />
              </div>
              <label className="checkbox-row">
                <input type="checkbox" checked={form.is_visible} onChange={(event) => setForm((current) => ({ ...current, is_visible: event.target.checked }))} />
                <span>Visible on homepage</span>
              </label>
              <div className="form-actions">
                <button type="button" onClick={() => setModalOpen(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Save changes" : "Create news item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(emptyPropertyForm);

  const loadProperties = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await getProperties();
      setProperties(Array.isArray(payload) ? payload : []);
    } catch (requestError) {
      setError(requestError.message || "Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProperties();
  }, []);

  const openCreate = () => {
    setEditingId("");
    setForm(emptyPropertyForm);
    setModalOpen(true);
  };

  const openEdit = (property) => {
    setEditingId(property.id);
    setForm({
      property_code: property.property_code || "",
      title: property.title || "",
      asking_price: property.asking_price ?? "",
      cap_rate: property.cap_rate ?? "",
      net_rent_monthly: property.net_rent_monthly ?? "",
      tenant_name: property.tenant_name || "",
      lease_expires_at: property.lease_expires_at ? String(property.lease_expires_at).slice(0, 10) : "",
      rent_type: property.rent_type || "",
      landlord_responsibility: property.landlord_responsibility || "",
      year_built: property.year_built ?? "",
      city: property.city || "",
      state: property.state || "",
      status: property.status || "draft",
      description: property.description || "",
    });
    setModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (editingId) await updateProperty(editingId, form);
      else await createProperty(form);
      setModalOpen(false);
      await loadProperties();
    } catch (requestError) {
      setError(requestError.message || "Failed to save property");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (propertyId) => {
    if (!window.confirm("Delete this property?")) return;
    try {
      await deleteProperty(propertyId);
      await loadProperties();
    } catch (requestError) {
      setError(requestError.message || "Failed to delete property");
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">Properties</h1>
          <p className="page-hint">Manage the listing inventory rendered throughout the site.</p>
        </div>
        <button type="button" className="btn-primary" onClick={openCreate}>
          Add property
        </button>
      </div>

      {loading ? <div className="page-loading">Loading properties...</div> : null}
      {error ? <div className="page-error">{error}</div> : null}

      {!loading ? (
        <section className="admin-card">
          <div className="table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Title</th>
                  <th>Tenant</th>
                  <th>CAP Rate</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {properties.map((property) => (
                  <tr key={property.id}>
                    <td>{property.property_code}</td>
                    <td>{property.title}</td>
                    <td>{property.tenant_name || "N/A"}</td>
                    <td>{property.cap_rate ?? "N/A"}</td>
                    <td>
                      <span className={`status-pill ${property.status}`}>{property.status}</span>
                    </td>
                    <td className="row-actions">
                      <button type="button" onClick={() => openEdit(property)}>
                        Edit
                      </button>
                      <button type="button" className="danger-btn" onClick={() => handleDelete(property.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {modalOpen ? (
        <div className="modal-overlay" onClick={() => !saving && setModalOpen(false)}>
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <h3>{editingId ? "Edit property" : "Add property"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="subcard-grid">
                <div className="form-row">
                  <label>Property code</label>
                  <input required value={form.property_code} onChange={(event) => setForm((current) => ({ ...current, property_code: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Status</label>
                  <select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="under_contract">Under contract</option>
                    <option value="sold">Sold</option>
                  </select>
                </div>
                <div className="form-row">
                  <label>Title</label>
                  <input required value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Tenant</label>
                  <input value={form.tenant_name} onChange={(event) => setForm((current) => ({ ...current, tenant_name: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Asking price</label>
                  <input value={form.asking_price} onChange={(event) => setForm((current) => ({ ...current, asking_price: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>CAP rate</label>
                  <input value={form.cap_rate} onChange={(event) => setForm((current) => ({ ...current, cap_rate: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Net rent monthly</label>
                  <input value={form.net_rent_monthly} onChange={(event) => setForm((current) => ({ ...current, net_rent_monthly: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Lease expires</label>
                  <input type="date" value={form.lease_expires_at} onChange={(event) => setForm((current) => ({ ...current, lease_expires_at: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Rent type</label>
                  <input value={form.rent_type} onChange={(event) => setForm((current) => ({ ...current, rent_type: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Landlord responsibility</label>
                  <input value={form.landlord_responsibility} onChange={(event) => setForm((current) => ({ ...current, landlord_responsibility: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>Year built</label>
                  <input value={form.year_built} onChange={(event) => setForm((current) => ({ ...current, year_built: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>City</label>
                  <input value={form.city} onChange={(event) => setForm((current) => ({ ...current, city: event.target.value }))} />
                </div>
                <div className="form-row">
                  <label>State</label>
                  <input value={form.state} onChange={(event) => setForm((current) => ({ ...current, state: event.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <label>Description</label>
                <textarea rows={5} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setModalOpen(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Save changes" : "Create property"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AdminsPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(emptyAdminForm);

  const loadAdmins = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await getUsers();
      setAdmins(Array.isArray(payload) ? payload : []);
    } catch (requestError) {
      setError(requestError.message || "Failed to load admins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const openCreate = () => {
    setEditingId("");
    setForm(emptyAdminForm);
    setModalOpen(true);
  };

  const openEdit = (admin) => {
    setEditingId(admin.id);
    setForm({
      full_name: admin.full_name || "",
      email: admin.email || "",
      password: "",
      role: admin.role || "admin",
      is_active: Boolean(admin.is_active),
    });
    setModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      const payload = { ...form };
      if (editingId && !payload.password) delete payload.password;
      if (editingId) await updateUser(editingId, payload);
      else await createUser(payload);
      setModalOpen(false);
      await loadAdmins();
    } catch (requestError) {
      setError(requestError.message || "Failed to save admin");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">Admins</h1>
          <p className="page-hint">Manage the people allowed to sign into the admin panel.</p>
        </div>
        <button type="button" className="btn-primary" onClick={openCreate}>
          Add admin
        </button>
      </div>

      {loading ? <div className="page-loading">Loading admins...</div> : null}
      {error ? <div className="page-error">{error}</div> : null}

      {!loading ? (
        <section className="admin-card">
          <div className="table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last login</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <tr key={admin.id}>
                    <td>{admin.full_name}</td>
                    <td>{admin.email}</td>
                    <td>{admin.role || "editor"}</td>
                    <td>
                      <span className={`status-pill ${admin.is_active ? "active" : "draft"}`}>
                        {admin.is_active ? "active" : "inactive"}
                      </span>
                    </td>
                    <td>{admin.last_login_at ? new Date(admin.last_login_at).toLocaleString() : "Never"}</td>
                    <td className="row-actions">
                      <button type="button" onClick={() => openEdit(admin)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {modalOpen ? (
        <div className="modal-overlay" onClick={() => !saving && setModalOpen(false)}>
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <h3>{editingId ? "Edit admin" : "Add admin"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <label>Full name</label>
                <input required value={form.full_name} onChange={(event) => setForm((current) => ({ ...current, full_name: event.target.value }))} />
              </div>
              <div className="form-row">
                <label>Email</label>
                <input required type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} />
              </div>
              <div className="form-row">
                <label>Password {editingId ? "(leave blank to keep current password)" : ""}</label>
                <input type="password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} />
              </div>
              <div className="form-row">
                <label>Role</label>
                <select value={form.role} onChange={(event) => setForm((current) => ({ ...current, role: event.target.value }))}>
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                </select>
              </div>
              <label className="checkbox-row">
                <input type="checkbox" checked={form.is_active} onChange={(event) => setForm((current) => ({ ...current, is_active: event.target.checked }))} />
                <span>Admin is active</span>
              </label>
              <div className="form-actions">
                <button type="button" onClick={() => setModalOpen(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Save changes" : "Create admin"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ForbiddenPage({ onNavigate }) {
  return (
    <div className="login-page">
      <div className="login-card">
        <h1>Admin session required</h1>
        <p>Sign in first to access the Trophy NNN Assets admin panel.</p>
        <button type="button" onClick={() => onNavigate("/login")}>
          Go to login
        </button>
      </div>
    </div>
  );
}

export default function AdminApp({ path, onNavigate }) {
  const [user, setUser] = useState(() => readStoredUser());

  useEffect(() => {
        if (!getAdminToken()) return undefined;

    let ignore = false;

        getCurrentAdmin()
      .then((payload) => {
        if (!ignore) {
          storeSession({ token: getAdminToken(), user: payload.user });
          setUser(payload.user);
        }
      })
      .catch(() => {
        if (!ignore) {
          clearSession();
          setUser(null);
          if (path !== "/login") onNavigate("/login");
        }
      });

    return () => {
      ignore = true;
    };
  }, [onNavigate, path]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // ignore logout failures and clear local state anyway
    }

    clearSession();
    setUser(null);
    onNavigate("/login");
  };

  const page = useMemo(() => {
    if (path === "/login") {
      return <AdminLoginPage onNavigate={onNavigate} onSessionChange={setUser} />;
    }

    if (!getAdminToken()) {
      return <ForbiddenPage onNavigate={onNavigate} />;
    }

    if (path === "/admin/general") return <GeneralSettingsPage />;
    if (path === "/admin/navigation") return <NavigationPage />;
    if (path === "/admin/homepage-hero") {
      return (
        <SectionEditorPage
          pageKey="home"
          sectionKey="hero"
          sectionType="hero"
          title="Homepage Hero"
          description="Edit the homepage hero image, heading, and supporting text."
          fields={[
            { name: "headline", label: "Hero heading", defaultValue: "Specialized in High CAP Investment Properties" },
            { name: "subheadline", label: "Hero subheadline", type: "textarea", rows: 4, defaultValue: "" },
            { name: "background_image_url", label: "Background image URL", defaultValue: "" },
            { name: "background_image_alt", label: "Background image alt text", defaultValue: "Commercial city skyline" },
          ]}
        />
      );
    }
    if (path === "/admin/homepage-property-intro") {
      return (
        <SectionEditorPage
          pageKey="home"
          sectionKey="property_intro"
          title="Homepage Property Intro"
          description="Control the small summary text and button above the property table."
          fields={[
            { name: "summary_text", label: "Summary text", type: "textarea", rows: 4, defaultValue: "" },
            { name: "button_label", label: "Button label", defaultValue: "Click for info" },
            { name: "button_color", label: "Button color", type: "color", defaultValue: "#EBB866" },
            { name: "button_action", label: "Button action", defaultValue: "scroll_to_contact" },
          ]}
        />
      );
    }
    if (path === "/admin/homepage-investor-focus") {
      return (
        <SectionEditorPage
          pageKey="home"
          sectionKey="investor_focus"
          title="Homepage Investor Focus"
          description="Edit the investor focus block and spotlight items on the homepage."
          fields={[
            { name: "eyebrow", label: "Eyebrow", defaultValue: "Investor Focus" },
            { name: "title", label: "Section title", defaultValue: "Blue-chip net-lease opportunities" },
            { name: "description", label: "Section description", type: "textarea", rows: 4, defaultValue: "" },
            {
              name: "spotlight_items",
              label: "Spotlight items",
              type: "list",
              itemLabel: "Spotlight item",
              defaultValue: [
                "High CAP opportunities with blue-chip tenancy",
                "Absolute NNN and NNN lease structures",
                "1031 exchange focused advisory",
                "Nationwide state-by-state inventory",
              ],
            },
          ]}
        />
      );
    }
    if (path === "/admin/homepage-table-columns") return <TableColumnsPage />;
    if (path === "/admin/articles") return <ArticlesPage />;
    if (path === "/admin/news") return <NewsPage />;
    if (path === "/admin/properties") return <PropertiesPage />;
    if (path === "/admin/admins") return <AdminsPage />;
    return <DashboardPage />;
  }, [onNavigate, path]);

  if (path === "/login") return page;
  if (!getAdminToken()) return page;

  return (
    <AdminLayout currentPath={path} onNavigate={onNavigate} user={user} onLogout={handleLogout}>
      {page}
    </AdminLayout>
  );
}

export { isAdminRoute };
